/**
 * Anonymized bid history for a lot.
 *
 * Reads the `get_lot_bid_history` RPC rather than the `bids` table, and that is
 * not an optimisation — it is the only way this can work at all. Row-level
 * security on `bids` exposes the caller's OWN rows and nothing else, so a rival's
 * activity is simply invisible to a client query. The RPC is a narrow
 * SECURITY DEFINER projection that returns amount, timestamp and "is this mine",
 * and deliberately cannot return the secret ceiling, identity, IP or user-agent.
 *
 * Lives in the shared client so the website and the app render the same history
 * from the same source. A second hand-written query is how the two would come to
 * disagree about what a bidder is allowed to see.
 */
import type { AuctisyncSupabase } from './client.js';

export interface LotBidHistoryEntry {
  id: string;
  /**
   * NULL when the sale sets `hide_winning_info` and this bid is somebody
   * else's — the amount is unknown to THIS caller, which is what null means.
   * A caller's own amounts are never masked. Render it as an em dash; never
   * sum it, and never coalesce it to 0, which is a real bid amount.
   */
  amountCents: number | null;
  createdAt: string;
  /** The signed-in caller placed this one. Decided server-side. */
  isYou: boolean;
  /** Placed automatically by the proxy on someone's behalf. */
  isProxy: boolean;
}

interface RawRow {
  bid_id: string;
  amount_cents: number | string | null;
  created_at: string;
  is_you: boolean;
  is_proxy: boolean;
}

export interface LotBidHistoryResult {
  entries: LotBidHistoryEntry[];
  error: string | null;
}

/**
 * Fetch the most recent bids on a lot, newest first.
 *
 * Returns an empty list rather than throwing when the lot is not publicly
 * visible — from the caller's point of view "no history" and "not allowed to see
 * this lot" are the same answer, and distinguishing them would itself leak
 * whether a hidden lot exists.
 */
export async function fetchLotBidHistory(
  supabase: AuctisyncSupabase,
  lotId: string,
  limit = 20,
): Promise<LotBidHistoryResult> {
  try {
    const { data, error } = await supabase.rpc('get_lot_bid_history', {
      p_lot_id: lotId,
      p_limit: limit,
    });

    if (error) {
      return { entries: [], error: error.message ?? 'bid_history_failed' };
    }

    const rows = (data ?? []) as RawRow[];
    return {
      entries: rows.map((r) => ({
        id: r.bid_id,
        // `bigint` arrives as a string over PostgREST once it exceeds the safe
        // integer range; Number() on the string is correct either way and
        // avoids a silent NaN on a large hammer price.
        amountCents:
          r.amount_cents === null
            ? null
            : typeof r.amount_cents === 'string'
              ? Number(r.amount_cents)
              : r.amount_cents,
        createdAt: r.created_at,
        isYou: r.is_you === true,
        isProxy: r.is_proxy === true,
      })),
      error: null,
    };
  } catch (err) {
    return { entries: [], error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Recent bids across a WHOLE auction — the live room's activity ticker.
 *
 * Same source, same rules, same shape as `fetchLotBidHistory`: a narrow
 * SECURITY DEFINER projection that returns amount, time, lot number and "is
 * this mine", and cannot return a ceiling, an identity, an IP or a user agent.
 * A direct query would show a bidder only their OWN bids, because RLS on
 * `bids` confines the client to their own rows — which is precisely what makes
 * a rival's activity invisible without us having to filter it.
 *
 * Returns an empty list rather than throwing, for the same reason as the
 * per-lot reader: to a caller, "no activity" and "not allowed to see this sale"
 * are the same answer, and distinguishing them would itself leak whether a
 * hidden sale exists.
 */
export interface AuctionBidHistoryEntry extends LotBidHistoryEntry {
  lotId: string;
  lotNumber: number;
}

interface RawAuctionRow extends RawRow {
  lot_id: string;
  lot_number: number | string;
}

export async function fetchAuctionBidHistory(
  supabase: AuctisyncSupabase,
  auctionId: string,
  limit = 20,
): Promise<{ entries: AuctionBidHistoryEntry[]; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('get_auction_bid_history', {
      p_auction_id: auctionId,
      p_limit: limit,
    });

    if (error) {
      return { entries: [], error: error.message ?? 'bid_history_failed' };
    }

    const rows = (data ?? []) as RawAuctionRow[];
    return {
      entries: rows.map((r) => ({
        id: r.bid_id,
        lotId: r.lot_id,
        lotNumber: typeof r.lot_number === 'string' ? Number(r.lot_number) : r.lot_number,
        amountCents:
          r.amount_cents === null
            ? null
            : typeof r.amount_cents === 'string'
              ? Number(r.amount_cents)
              : r.amount_cents,
        createdAt: r.created_at,
        isYou: r.is_you === true,
        isProxy: r.is_proxy === true,
      })),
      error: null,
    };
  } catch (err) {
    return { entries: [], error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Recent bids across EVERY sale the caller is involved in — the watchlist's
 * activity ticker.
 *
 * 🔴 WHY THIS IS NOT `fetchAuctionBidHistory` IN A LOOP. The watchlist is the
 * one page that genuinely spans sales, so it has no auction id to pass. Calling
 * the per-sale reader once per sale would fan out one round trip per auction
 * the bidder happens to be active in — unbounded, on a page they open
 * constantly. `get_my_activity_feed` takes the same projection and the same
 * privacy rules and scopes them by RELATIONSHIP instead: lots the caller leads,
 * has bid on, or is watching.
 *
 * Each row is judged against ITS OWN sale's privacy flags server-side, so a
 * bidder who is in a private sale and an open one gets each sale's rules on its
 * own rows rather than one sale's rules on all of them.
 *
 * Same contract as the other two readers: never throws, and an unreadable feed
 * is an empty one.
 */
export async function fetchMyActivityFeed(
  supabase: AuctisyncSupabase,
  limit = 20,
): Promise<{ entries: AuctionBidHistoryEntry[]; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('get_my_activity_feed', { p_limit: limit });

    if (error) {
      return { entries: [], error: error.message ?? 'activity_feed_failed' };
    }

    const rows = (data ?? []) as RawAuctionRow[];
    return {
      entries: rows.map((r) => ({
        id: r.bid_id,
        lotId: r.lot_id,
        lotNumber: typeof r.lot_number === 'string' ? Number(r.lot_number) : r.lot_number,
        amountCents:
          r.amount_cents === null
            ? null
            : typeof r.amount_cents === 'string'
              ? Number(r.amount_cents)
              : r.amount_cents,
        createdAt: r.created_at,
        isYou: r.is_you === true,
        isProxy: r.is_proxy === true,
      })),
      error: null,
    };
  } catch (err) {
    return { entries: [], error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * How a history row is labelled.
 *
 * The design handoff is explicit: "every other participant is simply 'bidder';
 * only the signed-in user is named". Centralised here so the phone and the
 * website cannot drift into showing different amounts of information about the
 * same event — and so there is exactly one place to audit.
 */
export function bidHistoryLabel(entry: Pick<LotBidHistoryEntry, 'isYou'>): 'you' | 'bidder' {
  return entry.isYou ? 'you' : 'bidder';
}

/**
 * How long ago a bid landed, in the design's compact form: `12s`, `1:14`, `2h`,
 * `3d`.
 *
 * ELAPSED, not a clock time, because the question a bidder is actually asking is
 * "is this lot moving right now?" — and `14:23` answers that only if you also
 * know what time it is and which timezone the row is in.
 *
 * `nowMs` is injected rather than read from `Date.now()` so callers can pass the
 * SERVER-corrected clock. A phone running a few minutes fast would otherwise
 * report bids as older than they are, which on a closing lot is exactly the
 * wrong way to be wrong.
 */
export function bidAgeLabel(createdAt: string, nowMs: number): string {
  const then = Date.parse(createdAt);
  if (!Number.isFinite(then)) return '';
  // Clamped at zero: a bid can appear a shade in the future when the server
  // clock and the device disagree, and "-3s" reads as a bug.
  const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));

  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m)}:${String(s).padStart(2, '0')}`;
  }
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3600))}h`;
  return `${String(Math.floor(seconds / 86_400))}d`;
}
